import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import axios from 'axios';
import { Download, Search } from 'react-feather';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { Spinner } from '../../../../AbstractElements';
import config from '../../../../config';

import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import '../admin-v3.css';
import v3DataTableStyles from '../v3DataTable';
import v3SelectStyles from '../v3Select';

const portalProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    styles: v3SelectStyles,
};

const REPORT_COLUMNS = [
    { header: 'Date', key: 'created_date', format: (v) => moment(v).format('DD/MM/YYYY HH:mm:ss') },
    { header: 'Module', key: 'module' },
    { header: 'Created By', key: 'createdByName' },
    { header: 'Activity', key: 'task' },
];

const ViewLog = () => {
    const { t } = useTranslation();
    const canView = localStorage.getItem('accessLogs') === '1';
    const concessionaire_id = localStorage.getItem('concessionaireId');
    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };
    const today = new Date().toISOString().split('T')[0];

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState('');
    const [reportData, setReportData] = useState([]);
    const [validationMessage, setValidationMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!canView) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canView]);

    useEffect(() => {
        axios.post(`${config.hostname}/users/getuserbyconcessionaire`, { concessionaire_id }, configheader)
            .then((res) => setUsers(res.data || [])).catch((e) => console.log(e));
    }, []);

    const userOptions = useMemo(
        () => [{ value: '', label: t('All users') }, ...users.map((u) => ({ value: u._id, label: u.userFirstName }))],
        [users, t],
    );

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, { created_by: login_id, concessionaire_id, module: 'Admin', task: login_name + task }, configheader)
            .catch((e) => console.log(e));
    };

    const generateReport = (e) => {
        e.preventDefault();
        if (endDate < startDate) {
            setValidationMessage(t('End date cannot be earlier than start date'));
            return;
        }
        setValidationMessage('');
        setLoading(true);
        axios.post(`${config.hostname}/log/report`, { concessionaire_id, start_date: startDate, end_date: endDate, created_by: userId }, configheader)
            .then((res) => { setReportData(res.data || []); insertToLog(' generated log report'); })
            .catch((err) => { toast.error(t('Error generating report')); console.log(err); })
            .finally(() => setLoading(false));
    };

    // Flatten the nested created_by name for table + export.
    const rows = useMemo(
        () => reportData.map((it) => ({ ...it, createdByName: it.created_by?.userFirstName || '' })),
        [reportData],
    );

    // Client-side search across every visible column.
    const filteredRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            moment(r.created_date).format('DD/MM/YYYY HH:mm:ss').toLowerCase().includes(q)
            || (r.module || '').toLowerCase().includes(q)
            || (r.createdByName || '').toLowerCase().includes(q)
            || (r.task || '').toLowerCase().includes(q));
    }, [rows, query]);

    const handleExport = (format) => {
        if (!filteredRows.length) return;
        const heading = t('Log Report');
        const range = `${t('From')}: ${startDate || '—'}  ${t('To')}: ${endDate || '—'}`;
        const data = filteredRows.map((it) => REPORT_COLUMNS.map((c) => (c.format ? c.format(it[c.key]) : it[c.key] ?? '')));
        const headers = REPORT_COLUMNS.map((c) => t(c.header));

        if (format === 'excel') {
            const ws = XLSX.utils.aoa_to_sheet([[heading], [range], [], headers, ...data]);
            ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Log Report');
            XLSX.writeFile(wb, 'LogReport.xlsx');
            insertToLog(' exported log report to excel');
        } else {
            const doc = new jsPDF();
            doc.setFontSize(13); doc.setFont('helvetica', 'bold');
            doc.text(heading, doc.internal.pageSize.getWidth() / 2, 10, { align: 'center' });
            doc.setFontSize(8); doc.setFont('helvetica', 'normal');
            doc.text(range, 10, 18);
            doc.autoTable({ startY: 24, head: [headers], body: data, styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' }, headStyles: { fontSize: 7 } });
            doc.save('LogReport.pdf');
            insertToLog(' exported log report to pdf');
        }
    };

    const columns = [
        { name: t('Date'), selector: (r) => r.created_date, sortable: true, minWidth: '170px', format: (r) => moment(r.created_date).format('DD/MM/YYYY HH:mm:ss') },
        { name: t('Module'), selector: (r) => r.module, sortable: true, minWidth: '130px' },
        { name: t('Created By'), selector: (r) => r.createdByName, sortable: true, minWidth: '140px' },
        { name: t('Activity'), selector: (r) => r.task, sortable: true, wrap: true, grow: 2 },
    ];

    if (!canView) return null;

    return (
        <Fragment>
            <div className="v3-sadmin v3-admin-page">
                {/* Compact hero with the filter form inline — one tight row. */}
                <div className="v3-sadmin-hero v3-logs-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>{t('Admin settings')}</p>
                        <h1 className="v3-display v3-sadmin-greeting"><span className="v3-sadmin-name">{t('Activity Logs')}</span></h1>
                    </div>
                    <form className="v3-logs-filters" onSubmit={generateReport}>
                        <div className="v3-field">
                            <label>{t('Start Date')}</label>
                            <input className="form-control" type="date" value={startDate} max={today} onChange={(e) => { setStartDate(e.target.value); setReportData([]); }} required />
                        </div>
                        <div className="v3-field">
                            <label>{t('End Date')}</label>
                            <input className="form-control" type="date" value={endDate} max={today} onChange={(e) => { setEndDate(e.target.value); setReportData([]); }} required />
                        </div>
                        <div className="v3-field v3-logs-user">
                            <label>{t('User')}</label>
                            <Select {...portalProps} options={userOptions}
                                value={userOptions.find((o) => o.value === userId) || userOptions[0]}
                                onChange={(opt) => { setUserId(opt ? opt.value : ''); setReportData([]); }}
                                isSearchable placeholder={t('All users')} />
                        </div>
                        <button type="submit" className="v3-btn v3-btn-primary v3-logs-generate">
                            <Search size={15} /> {t('Generate')}
                        </button>
                    </form>
                </div>
                {validationMessage && <p style={{ color: 'var(--v3-text-danger, #ef4444)', margin: '-8px 0 0', fontSize: 13 }}>{validationMessage}</p>}

                <div className="v3-card v3-sadmin-list-card">
                    <div className="v3-sadmin-list-header">
                        <div className="v3-sadmin-list-title">
                            <h2 className="v3-title">{t('Log Entries')}</h2>
                            <span className="v3-sadmin-list-count">{query.trim() ? `${filteredRows.length}/${rows.length}` : rows.length}</span>
                        </div>
                        <div className="v3-sadmin-list-actions">
                            <label className="v3-sadmin-search">
                                <Search size={15} />
                                <input type="text" placeholder={t('Search logs…')} value={query} onChange={(e) => setQuery(e.target.value)} disabled={!rows.length} aria-label={t('Search logs')} />
                            </label>
                            <button type="button" className="v3-btn v3-btn-ghost" onClick={() => handleExport('excel')} disabled={!filteredRows.length}>
                                <Download size={14} /> {t('Excel')}
                            </button>
                            <button type="button" className="v3-btn v3-btn-ghost" onClick={() => handleExport('pdf')} disabled={!filteredRows.length}>
                                <Download size={14} /> {t('PDF')}
                            </button>
                        </div>
                    </div>
                    <div style={{ padding: '4px 8px 8px' }}>
                        {loading ? (
                            <div className="loader-box"><Spinner attrSpinner={{ className: 'loader-3' }} /></div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={filteredRows}
                                pagination
                                paginationPerPage={50}
                                highlightOnHover
                                responsive
                                customStyles={v3DataTableStyles}
                                noDataComponent={rows.length ? t('No logs match your search.') : t('Generate a report to view logs.')}
                            />
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default ViewLog;
